import { useEffect, useMemo, useState } from "react";
import { Activity, AlertTriangle, Download, PhoneCall, Radio, RefreshCw, ShieldCheck, Wifi, WifiOff } from "lucide-react";
import { api, fmtDuration, translateHangupCause } from "@/lib/api";

function Stat({ label, value, sub, icon: Icon, tone = "" }) {
  return (
    <div className="sbc-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</div>
          <div className={`mt-2 text-2xl font-mono font-semibold ${tone}`}>{value}</div>
          {sub && <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">{sub}</div>}
        </div>
        <Icon size={18} className="text-[color:var(--text-secondary)]" />
      </div>
    </div>
  );
}

function pct(value) {
  return `${Number(value || 0).toFixed(2)}%`;
}

export default function OperationalQuality() {
  const [filters, setFilters] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const { data: response } = await api.get("/operational-quality", { params });
      setData(response);
    } catch (err) {
      setError(err.response?.data?.detail || "Falha ao carregar relatorio de qualidade operacional.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = data?.summary || {};
  const lowAvailability = useMemo(() => (
    (data?.extension_uptime || []).filter((row) => Number(row.availability_percent || 0) < 95).slice(0, 20)
  ), [data]);

  const exportCsv = () => {
    const rows = data?.extension_uptime || [];
    const header = ["Ramal", "Nome", "Cliente", "Status", "Tempo registrado", "Tempo fora", "Disponibilidade", "IP", "User agent"];
    const lines = [header.join(";")];
    rows.forEach((row) => {
      lines.push([
        row.numero || "",
        row.nome || "",
        row.tenant_name || "",
        row.online ? "Registrado" : "Fora",
        fmtDuration(row.registered_sec),
        fmtDuration(row.offline_sec),
        pct(row.availability_percent),
        row.last_ip || "",
        row.user_agent || "",
      ].map((value) => `"${String(value).replaceAll('"', '""')}"`).join(";"));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "voxyra-qualidade-ramais.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-5" data-testid="operational-quality-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Relatorio operacional</div>
          <h1 className="text-2xl font-semibold tracking-tight">Qualidade operacional</h1>
          <p className="mt-1 text-sm text-[color:var(--text-secondary)]">
            Chamadas, falhas SIP/troncos e disponibilidade dos ramais.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Inicio
            <input type="date" className="sbc-input mt-1 block" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Fim
            <input type="date" className="sbc-input mt-1 block" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} />
          </label>
          <button className="sbc-btn sbc-btn-primary" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
          <button className="sbc-btn" onClick={exportCsv} disabled={!data}>
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      {error && <div className="rounded-sm border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 text-xs text-[color:var(--accent-red)]">{error}</div>}

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-6">
        <Stat icon={PhoneCall} label="Chamadas" value={summary.total_calls || 0} sub={`${summary.answered || 0} atendidas`} />
        <Stat icon={ShieldCheck} label="Taxa atendimento" value={pct(summary.answer_rate)} sub="ASR do periodo" tone="text-[color:var(--accent-green)]" />
        <Stat icon={AlertTriangle} label="Falhas" value={summary.failed || 0} sub={`${summary.not_answered || 0} nao atendidas`} tone="text-[color:var(--accent-red)]" />
        <Stat icon={Wifi} label="Ramais online" value={`${summary.extensions_online || 0}/${summary.extensions_total || 0}`} sub="registro atual" tone="text-[color:var(--accent-green)]" />
        <Stat icon={WifiOff} label="Tempo fora" value={`${summary.offline_hours || 0}h`} sub="periodo medido" tone="text-[color:var(--accent-amber)]" />
        <Stat icon={Radio} label="Troncos" value={`${summary.trunks_online || 0}/${summary.trunks_total || 0}`} sub="status atual" />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.5fr)_420px]">
        <section className="sbc-card overflow-hidden">
          <div className="border-b border-[color:var(--border-default)] p-4">
            <h2 className="font-semibold">Tempo registrado / fora de servico por ramal</h2>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">A contagem historica passa a acumular a partir desta publicacao.</p>
          </div>
          <div className="overflow-auto max-h-[560px]">
            <table className="sbc-table min-w-[1100px]">
              <thead>
                <tr>
                  <th>Ramal</th>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Registrado</th>
                  <th>Fora de servico</th>
                  <th>Disponibilidade</th>
                  <th>Desde</th>
                  <th>IP</th>
                  <th>Equipamento</th>
                </tr>
              </thead>
              <tbody>
                {(data?.extension_uptime || []).length ? data.extension_uptime.map((row) => (
                  <tr key={row.extension_id}>
                    <td className="font-mono font-semibold">{row.numero}</td>
                    <td>{row.nome || "-"}</td>
                    <td><span className={`sbc-tag ${row.online ? "tag-online" : "tag-danger"}`}>{row.online ? "Registrado" : "Fora"}</span></td>
                    <td className="font-mono">{fmtDuration(row.registered_sec)}</td>
                    <td className="font-mono">{fmtDuration(row.offline_sec)}</td>
                    <td className="font-mono">{pct(row.availability_percent)}</td>
                    <td className="font-mono text-xs">{row.state_since ? new Date(row.state_since).toLocaleString("pt-BR") : "-"}</td>
                    <td className="font-mono text-xs">{row.last_ip || "-"}</td>
                    <td className="text-xs">{row.user_agent || "-"}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={9} className="py-8 text-center text-[color:var(--text-muted)]">Sem ramais para exibir.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="space-y-5">
          <section className="sbc-card p-4">
            <div className="mb-3 flex items-center gap-2 font-semibold"><Activity size={15} /> Ramais com menor disponibilidade</div>
            <div className="space-y-2">
              {lowAvailability.length ? lowAvailability.map((row) => (
                <div key={row.extension_id} className="flex items-center justify-between gap-3 rounded-sm border border-[color:var(--border-default)] px-3 py-2 text-sm">
                  <span><span className="font-mono">{row.numero}</span> {row.nome}</span>
                  <strong className="font-mono text-[color:var(--accent-amber)]">{pct(row.availability_percent)}</strong>
                </div>
              )) : <div className="text-xs text-[color:var(--text-muted)]">Nenhum ramal abaixo de 95% no periodo medido.</div>}
            </div>
          </section>

          <section className="sbc-card p-4">
            <div className="mb-3 font-semibold">Falhas por causa</div>
            <div className="space-y-2">
              {(data?.failure_causes || []).length ? data.failure_causes.map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3 rounded-sm border border-[color:var(--border-default)] px-3 py-2 text-sm">
                  <span>{translateHangupCause(row.label)}</span>
                  <strong className="font-mono">{row.count}</strong>
                </div>
              )) : <div className="text-xs text-[color:var(--text-muted)]">Sem falhas no periodo.</div>}
            </div>
          </section>

          <section className="sbc-card p-4">
            <div className="mb-3 font-semibold">Troncos / operadoras</div>
            <div className="space-y-2">
              {(data?.trunks || []).slice(0, 12).map((row) => (
                <div key={row.trunk_name} className="rounded-sm border border-[color:var(--border-default)] px-3 py-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span>{row.trunk_name}</span>
                    <strong className="font-mono">{pct(row.success_rate)}</strong>
                  </div>
                  <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">{row.calls} chamadas · {row.failures} falhas · {row.minutes} min</div>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
